import { useState, useMemo } from 'react';
import { TrendingUp, Package, Store, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { CopyableField } from '../components/CopyableField';
import { useForecast } from '../hooks/useForecast';
import type { ForecastBucket, ForecastItem } from '../hooks/useForecast';

// --- Types ---

interface VendorWeekGroup {
  vendorName: string;
  weekStart: string;
  weekEnd: string;
  items: ForecastItem[];
  totalParts: number;
  totalQuantity: number;
}

// --- Helpers ---

function computeWeekEnd(weekStart: string): string {
  if (weekStart === 'unscheduled') return 'unscheduled';
  const start = parseISO(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return format(end, 'yyyy-MM-dd');
}

function formatWeekRange(weekStart: string, weekEnd: string): string {
  if (weekStart === 'unscheduled') return 'Unscheduled';
  return `${format(parseISO(weekStart), 'MMM d')} \u2013 ${format(parseISO(weekEnd), 'MMM d')}`;
}

function normalizeBuckets(rawData: unknown): VendorWeekGroup[] {
  // The API returns { weeks, cutoffDate, buckets: [...] } wrapped by success(),
  // and useForecast types it as { data: ForecastBucket[] }.
  // Handle both shapes gracefully.
  if (!rawData) return [];

  // If rawData is already an array (matching the ForecastResponse type)
  if (Array.isArray(rawData)) {
    return (rawData as ForecastBucket[]).map((b) => ({
      vendorName: b.vendorName ?? 'Unknown Vendor',
      weekStart: b.weekStart,
      weekEnd: b.weekEnd ?? computeWeekEnd(b.weekStart),
      items: b.items ?? [],
      totalParts: b.totalParts ?? b.items?.length ?? 0,
      totalQuantity: b.totalQuantity ?? 0,
    }));
  }

  // If rawData is an object with a buckets property (actual API shape)
  const obj = rawData as Record<string, unknown>;
  if (obj.buckets && Array.isArray(obj.buckets)) {
    return (obj.buckets as ForecastBucket[]).map((b) => ({
      vendorName: b.vendorName ?? 'Unknown Vendor',
      weekStart: b.weekStart,
      weekEnd: b.weekEnd ?? computeWeekEnd(b.weekStart),
      items: b.items ?? [],
      totalParts: b.totalParts ?? b.items?.length ?? 0,
      totalQuantity: b.totalQuantity ?? 0,
    }));
  }

  return [];
}

// --- Collapsible Bucket Component ---

function BucketSection({ bucket }: { bucket: VendorWeekGroup }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-background rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-background-subtle hover:bg-background-muted transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-foreground-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-foreground-muted" />
          )}
          <CopyableField
            value={bucket.vendorName}
            className="font-medium text-foreground"
          />
          <span className="text-xs text-foreground-muted">
            {formatWeekRange(bucket.weekStart, bucket.weekEnd)}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-foreground-muted bg-background px-2 py-0.5 rounded-full">
            {bucket.totalParts} part{bucket.totalParts !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-foreground-muted">
            {bucket.totalQuantity} total qty
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                  Part Number
                </th>
                <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                  Qty Needed
                </th>
                <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                  Need By
                </th>
                <th className="px-4 py-2 text-xs font-medium text-foreground-muted">
                  Projects
                </th>
              </tr>
            </thead>
            <tbody>
              {bucket.items.map((item, idx) => (
                <tr
                  key={`${item.partNumber}-${idx}`}
                  className="border-b border-border last:border-b-0 hover:bg-background-subtle transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <CopyableField value={item.partNumber} mono />
                  </td>
                  <td className="px-4 py-2.5 text-foreground">
                    {item.quantityNeeded}
                  </td>
                  <td className="px-4 py-2.5 text-foreground-muted">
                    {item.needByDate
                      ? format(parseISO(item.needByDate), 'MMM d, yyyy')
                      : '\u2014'}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {item.projects.map((proj, pIdx) => (
                        <CopyableField
                          key={`${proj.soNumber}-${pIdx}`}
                          value={proj.soNumber}
                          className="text-xs bg-background-subtle px-1.5 py-0.5 rounded"
                          mono
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Main Page Component ---

export function ForecastPage() {
  const [weeks, setWeeks] = useState(8);

  const { data: response, isLoading, error } = useForecast(weeks);

  // Normalize the response data into VendorWeekGroup[]
  const { buckets, stats } = useMemo(() => {
    const buckets = normalizeBuckets(response?.data);

    const vendorSet = new Set<string>();
    const partSet = new Set<string>();
    let totalQuantity = 0;

    for (const bucket of buckets) {
      vendorSet.add(bucket.vendorName);
      for (const item of bucket.items) {
        partSet.add(item.partNumber);
        totalQuantity += item.quantityNeeded;
      }
    }

    return {
      buckets,
      stats: {
        vendorCount: vendorSet.size,
        uniqueParts: partSet.size,
        totalQuantity,
      },
    };
  }, [response]);

  // --- Loading / Error states ---

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-56 bg-background-muted rounded animate-pulse" />
        <div className="h-4 w-80 bg-background-muted rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-background-muted rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="h-48 bg-background-muted rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Demand Forecast</h1>
        <div className="rounded-lg border border-danger bg-danger-subtle p-6 text-center">
          <p className="text-sm text-danger">
            Failed to load forecast: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Demand Forecast</h1>
          <p className="text-sm text-foreground-muted mt-1">
            Upcoming parts needs for order consolidation planning
          </p>
        </div>

        {/* Weeks Selector */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="weeks-select"
            className="text-sm text-foreground-muted"
          >
            Horizon:
          </label>
          <select
            id="weeks-select"
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            className="px-3 py-2 text-sm rounded-md border border-border bg-background text-foreground appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value={4}>4 weeks</option>
            <option value={8}>8 weeks</option>
            <option value={12}>12 weeks</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10">
              <Store className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {stats.vendorCount}
              </p>
              <p className="text-xs text-foreground-muted">Vendors with needs</p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-warning-subtle">
              <Package className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {stats.uniqueParts}
              </p>
              <p className="text-xs text-foreground-muted">Unique parts</p>
            </div>
          </div>
        </div>
        <div className="bg-background rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-success-subtle">
              <Layers className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">
                {stats.totalQuantity}
              </p>
              <p className="text-xs text-foreground-muted">Total quantity needed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor/Week Buckets */}
      {buckets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-12 text-center">
          <TrendingUp className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground-muted">
            No upcoming demand in the forecast period
          </p>
          <p className="text-xs text-foreground-subtle mt-1">
            Try extending the forecast horizon
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {buckets.map((bucket, idx) => (
            <BucketSection
              key={`${bucket.vendorName}-${bucket.weekStart}-${idx}`}
              bucket={bucket}
            />
          ))}
        </div>
      )}
    </div>
  );
}
